import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config';
import { Plus } from 'lucide-react';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';
import { ClosePanelButton } from './ClosePanelButton';
import { RotatingDeleteButton } from './RotatingDeleteButton';
import { ClipboardButton } from './ClipboardButton';
import { Link } from './ui/link';
import { PermissionSelectItem, permissionSelectContentClassName } from './PermissionSelectItem';
import { SingleSelectCombobox } from './ui/single-select-combobox';
import { useLocale } from '../contexts/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogPrimaryShortcut,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectTrigger,
} from './ui/select';
import { Input } from './ui/input';

interface ShareDocumentProps {
  token: string | null;
  documentId: string | undefined;
  projectId?: string | null;
  documentProjectId?: string | null;
  t: (key: string) => string;
  showToast: (message: string) => void;
  onClose?: () => void;
  hideHeading?: boolean;
}

type DocumentPermission = 'reader' | 'commenter' | 'editor';

interface DocumentProjectShareItem {
  id: string;
  projectId: string;
  permission: DocumentPermission;
  createdAt: string;
  projectTitle?: string;
  projectSharedWith?: Array<{
    userEmail: string;
    permission: 'reader' | 'commenter' | 'editor' | 'admin';
  }>;
}

export const ShareDocument: React.FC<ShareDocumentProps> = ({
  token,
  documentId,
  projectId,
  documentProjectId,
  t,
  showToast,
  onClose,
  hideHeading = false,
}) => {
  const { locale } = useLocale();
  const { showErrorToast } = useToast();
  const [shareLinks, setShareLinks] = useState<Array<{ id: string; token: string; createdAt: string; expiresAt: string | null }>>([]);
  const [isLoadingShareLinks, setIsLoadingShareLinks] = useState<boolean>(false);
  const [documentShares, setDocumentShares] = useState<Array<{ id: string; userEmail: string; permission: DocumentPermission; createdAt: string }>>([]);
  const [isLoadingShares, setIsLoadingShares] = useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
  const [shareEmail, setShareEmail] = useState<string>('');
  const [sharePermission, setSharePermission] = useState<DocumentPermission>('reader');
  const [isSharing, setIsSharing] = useState<boolean>(false);
  const [documentProjectShares, setDocumentProjectShares] = useState<DocumentProjectShareItem[]>([]);
  const [isLoadingProjectShares, setIsLoadingProjectShares] = useState<boolean>(false);
  const [shareableProjects, setShareableProjects] = useState<Array<{ id: string; title: string; ownership: 'owned' | 'shared' }>>([]);
  const [isProjectShareModalOpen, setIsProjectShareModalOpen] = useState<boolean>(false);
  const [selectedShareProjectId, setSelectedShareProjectId] = useState<string>('');
  const [projectSharePermission, setProjectSharePermission] = useState<DocumentPermission>('reader');
  const [isSharingWithProject, setIsSharingWithProject] = useState<boolean>(false);

  useEffect(() => {
    const fetchShareLinks = async (): Promise<void> => {
      if (!token || !documentId || documentId === 'new') {
        return;
      }

      setIsLoadingShareLinks(true);
      try {
        const response = await fetch(`${API_BASE}/api/documents/${documentId}/share-links`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setShareLinks(data.shareLinks || []);
        } else {
          console.error('Failed to load share links');
        }
      } catch (err) {
        console.error('Error fetching share links:', err);
      } finally {
        setIsLoadingShareLinks(false);
      }
    };

    fetchShareLinks();
  }, [token, documentId]);

  useEffect(() => {
    const fetchDocumentShares = async (): Promise<void> => {
      if (!token || !documentId || documentId === 'new') {
        return;
      }

      setIsLoadingShares(true);
      try {
        const response = await fetch(`${API_BASE}/api/documents/${documentId}/shares`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setDocumentShares(data.shares || []);
        } else {
          console.error('Failed to load document shares');
        }
      } catch (err) {
        console.error('Error fetching document shares:', err);
      } finally {
        setIsLoadingShares(false);
      }
    };

    fetchDocumentShares();
  }, [token, documentId]);

  useEffect(() => {
    const fetchDocumentProjectShares = async (): Promise<void> => {
      if (!token || !documentId || documentId === 'new') {
        return;
      }

      setIsLoadingProjectShares(true);
      try {
        const [sharesResponse, projectsResponse] = await Promise.all([
          fetch(`${API_BASE}/api/documents/${documentId}/project-shares`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE}/api/documents/${documentId}/shareable-projects`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (sharesResponse.ok) {
          const data = await sharesResponse.json();
          setDocumentProjectShares(data.shares || []);
        }
        if (projectsResponse.ok) {
          const data = await projectsResponse.json();
          setShareableProjects(data.projects || []);
        }
      } catch (err) {
        console.error('Error fetching document project shares:', err);
      } finally {
        setIsLoadingProjectShares(false);
      }
    };

    void fetchDocumentProjectShares();
  }, [token, documentId]);

  const refreshShareableProjects = async (): Promise<void> => {
    if (!token || !documentId || documentId === 'new') {
      return;
    }
    try {
      const projectsResponse = await fetch(`${API_BASE}/api/documents/${documentId}/shareable-projects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (projectsResponse.ok) {
        const data = await projectsResponse.json();
        setShareableProjects(data.projects || []);
      }
    } catch (err) {
      console.error('Error refreshing shareable projects:', err);
    }
  };

  const createShareLink = async (): Promise<void> => {
    if (!token || !documentId || documentId === 'new') {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/documents/${documentId}/share-links`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setShareLinks((prev) => [data, ...prev]);
        showToast(t('share_link_created'));
      } else {
        showErrorToast(t('failed_to_create_share_link'));
      }
    } catch (err) {
      console.error('Error creating share link:', err);
      showErrorToast(t('failed_to_create_share_link'));
    }
  };

  const deleteShareLink = async (shareLinkId: string): Promise<void> => {
    if (!token) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/share-links/${shareLinkId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setShareLinks((prev) => prev.filter((link) => link.id !== shareLinkId));
      } else {
        showErrorToast(t('failed_to_delete_share_link'));
      }
    } catch (err) {
      console.error('Error deleting share link:', err);
      showErrorToast(t('failed_to_delete_share_link'));
    }
  };

  const getShareUrl = (shareToken: string): string => {
    const projectIdForShare = projectId || documentProjectId;
    return projectIdForShare
      ? `${window.location.origin}/projects/${projectIdForShare}/documents/${documentId}?shareToken=${shareToken}`
      : `${window.location.origin}/documents/${documentId}?shareToken=${shareToken}`;
  };

  const copyShareLink = (shareToken: string): void => {
    navigator.clipboard.writeText(getShareUrl(shareToken)).then(() => {
      showToast(t('link_copied'));
    }).catch(() => {
      console.error('Failed to copy link');
    });
  };

  const handleShareWithUser = async (): Promise<void> => {
    if (!token || !documentId || documentId === 'new' || !shareEmail || isSharing) {
      return;
    }

    if (!shareEmail.includes('@')) {
      showErrorToast(t('invalid_email') || 'Invalid email address');
      return;
    }

    setIsSharing(true);
    try {
      const response = await fetch(`${API_BASE}/api/documents/${documentId}/share`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: shareEmail,
          permission: sharePermission,
          locale: locale,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setDocumentShares((prev) => [data.share, ...prev]);
        setShareEmail('');
        setSharePermission('reader');
        setIsShareModalOpen(false);
        showToast(t('document_shared') || 'Document shared successfully');
      } else {
        const errorData = await response.json().catch(() => ({}));
        showErrorToast(errorData.error || t('failed_to_share_document') || 'Failed to share document');
      }
    } catch (err) {
      console.error('Error sharing document:', err);
      showErrorToast(t('failed_to_share_document') || 'Failed to share document');
    } finally {
      setIsSharing(false);
    }
  };

  const documentPermissionLabel = (permission: 'reader' | 'commenter' | 'editor'): string => {
    if (permission === 'reader') return t('permission_reader') || 'Reader';
    if (permission === 'commenter') return t('permission_commenter') || 'Commenter';
    return t('permission_editor') || 'Editor';
  };

  const projectPermissionLabel = (permission: 'reader' | 'commenter' | 'editor' | 'admin'): string => {
    if (permission === 'reader') return t('permission_reader') || 'Reader';
    if (permission === 'commenter') return t('permission_commenter') || 'Commenter';
    if (permission === 'editor') return t('permission_editor') || 'Editor';
    return t('permission_admin') || 'Admin';
  };

  const handleDeleteShare = async (shareId: string): Promise<void> => {
    if (!token || !documentId || documentId === 'new') {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/documents/${documentId}/shares/${shareId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setDocumentShares((prev) => prev.filter((share) => share.id !== shareId));
        showToast(t('share_removed') || 'Share removed');
      } else {
        showErrorToast(t('failed_to_remove_share') || 'Failed to remove share');
      }
    } catch (err) {
      console.error('Error deleting share:', err);
      showErrorToast(t('failed_to_remove_share') || 'Failed to remove share');
    }
  };

  const handleShareWithProject = async (): Promise<void> => {
    if (!token || !documentId || documentId === 'new' || !selectedShareProjectId || isSharingWithProject) {
      return;
    }

    setIsSharingWithProject(true);
    try {
      const response = await fetch(`${API_BASE}/api/documents/${documentId}/project-shares`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectId: selectedShareProjectId,
          permission: projectSharePermission,
          locale,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setDocumentProjectShares((prev) => [data.share, ...prev]);
        setShareableProjects((prev) => prev.filter((project) => project.id !== selectedShareProjectId));
        setSelectedShareProjectId('');
        setProjectSharePermission('reader');
        setIsProjectShareModalOpen(false);
        showToast(t('document_shared_with_project') || 'Document shared with project');
      } else {
        const errorData = await response.json().catch(() => ({}));
        showErrorToast(errorData.error || t('failed_to_share_document_with_project') || 'Failed to share document with project');
      }
    } catch (err) {
      console.error('Error sharing document with project:', err);
      showErrorToast(t('failed_to_share_document_with_project') || 'Failed to share document with project');
    } finally {
      setIsSharingWithProject(false);
    }
  };

  const handleDeleteProjectShare = async (shareId: string): Promise<void> => {
    if (!token || !documentId || documentId === 'new') {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/documents/${documentId}/project-shares/${shareId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setDocumentProjectShares((prev) => prev.filter((share) => share.id !== shareId));
        await refreshShareableProjects();
        showToast(t('share_removed') || 'Share removed');
      } else {
        showErrorToast(t('failed_to_remove_share') || 'Failed to remove share');
      }
    } catch (err) {
      console.error('Error deleting project share:', err);
      showErrorToast(t('failed_to_remove_share') || 'Failed to remove share');
    }
  };

  const shareableProjectSections = [
    {
      label: t('projects_shared_with_you'),
      options: shareableProjects
        .filter((project) => project.ownership === 'shared')
        .map((project) => ({ value: project.id, label: project.title })),
    },
    {
      label: t('your_projects'),
      options: shareableProjects
        .filter((project) => project.ownership === 'owned')
        .map((project) => ({ value: project.id, label: project.title })),
    },
  ].filter((section) => section.options.length > 0);

  const projectSharingId = projectId || documentProjectId;
  const projectSharingHref = projectSharingId ? `/projects/${projectSharingId}/sharing` : null;
  const shareProjectHintParts = t('you_can_also_share_the_whole_project').split('{{link}}');

  return (
    <div style={{ minWidth: 0, width: '100%', maxWidth: '100%', overflow: 'hidden', contain: 'inline-size' }}>
      {hideHeading ? null : (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: projectSharingHref ? '0.5rem' : '2rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600, color: 'var(--foreground)' }}>{t('share_document')}</h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {onClose && (
              <ClosePanelButton onClick={onClose} ariaLabel={t('close')} />
            )}
          </div>
        </div>
      )}
      {projectSharingHref && (
        <p style={{ margin: '0 0 1.5rem', fontSize: '0.875rem', color: 'var(--muted-foreground)' }}>
          {shareProjectHintParts[0]}
          <Link
            to={projectSharingHref}
            className="font-medium text-primary underline underline-offset-2 hover:text-primary/80 hover:underline"
          >
            {t('share_the_whole_project')}
          </Link>
          {shareProjectHintParts[1] ?? ''}
        </p>
      )}
      <div style={{ marginBottom: '1rem' }}>
        <button
          onClick={createShareLink}
          disabled={!token || !documentId || documentId === 'new' || isLoadingShareLinks}
          style={{
            width: '100%',
            padding: '0.75rem 1rem',
            backgroundColor: 'var(--scribane-black)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: (!token || !documentId || documentId === 'new' || isLoadingShareLinks) ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem',
            fontWeight: '500',
            transition: 'background-color 0.2s',
            opacity: (!token || !documentId || documentId === 'new' || isLoadingShareLinks) ? 0.5 : 1,
            boxSizing: 'border-box',
          }}
          onMouseEnter={(e) => {
            if (token && documentId && documentId !== 'new' && !isLoadingShareLinks) {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#2563eb';
            }
          }}
          onMouseLeave={(e) => {
            if (token && documentId && documentId !== 'new' && !isLoadingShareLinks) {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--scribane-black)';
            }
          }}
        >
          {t('create_public_link')}
        </button>
      </div>
      {isLoadingShareLinks ? (
        <div>{t('loading')}</div>
      ) : shareLinks.length === 0 ? (
        <div style={{ color: 'var(--muted-foreground)', fontSize: '0.875rem' }}>{t('no_share_links')}</div>
      ) : (
        <div style={{ minWidth: 0, width: '100%', overflow: 'hidden' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.5rem',
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'var(--muted-foreground)',
              }}
            >
              {t('public_link')}
            </div>
            <div
              style={{
                flexShrink: 0,
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'var(--muted-foreground)',
              }}
            >
              {t('actions')}
            </div>
          </div>
          {shareLinks.map((link) => {
            const shareUrl = getShareUrl(link.token);
            return (
              <div
                key={link.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  minWidth: 0,
                  padding: '0.5rem 0',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <div
                  title={shareUrl}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    width: 0,
                    fontSize: '0.75rem',
                    color: 'var(--muted-foreground)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {shareUrl}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
                  <ClipboardButton
                    onClick={() => copyShareLink(link.token)}
                    tooltipText={t('copy_link')}
                    ariaLabel={t('copy_link')}
                  />
                  <RotatingDeleteButton
                    onClick={() => deleteShareLink(link.id)}
                    tooltipText={t('delete_link')}
                    ariaLabel={t('delete_link')}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div
        className="mt-12 grid min-w-0 grid-cols-1 gap-8 border-t border-border pt-12 md:grid-cols-2 md:items-start md:gap-6 [&>*]:min-w-0"
      >
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', gap: '0.75rem', flexWrap: 'wrap', minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: 'var(--foreground)', minWidth: 0 }}>{t('share_document_with_users')}</h3>
            <Button
              onClick={() => setIsShareModalOpen(true)}
              disabled={!token || !documentId || documentId === 'new'}
              size="sm"
              className="shrink-0"
            >
              <Plus className="h-4 w-4" />
              {t('share_document_with_new_user')}
            </Button>
          </div>

          {isLoadingShares ? (
            <div>{t('loading')}</div>
          ) : documentShares.length === 0 ? (
            <div style={{ color: 'var(--muted-foreground)', fontSize: '0.875rem' }}>{t('no_document_shares') || 'No users have been shared with this document'}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {documentShares.map((share) => (
                <div
                  key={share.id}
                  style={{
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    backgroundColor: 'var(--card)',
                    border: '1px solid var(--border)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--foreground)', marginBottom: '0.25rem' }}>
                      {share.userEmail}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)' }}>
                      {documentPermissionLabel(share.permission)}
                    </div>
                  </div>
                  <RotatingDeleteButton
                    onClick={() => handleDeleteShare(share.id)}
                    tooltipText="Remove this share"
                    ariaLabel={t('remove') || 'Remove'}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', gap: '0.75rem', flexWrap: 'wrap', minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: 'var(--foreground)', minWidth: 0 }}>{t('share_document_with_projects')}</h3>
            <Button
              onClick={() => setIsProjectShareModalOpen(true)}
              disabled={!token || !documentId || documentId === 'new' || shareableProjects.length === 0}
              size="sm"
              className="shrink-0"
            >
              <Plus className="h-4 w-4" />
              {t('share_document_with_new_project')}
            </Button>
          </div>

          <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: 'var(--muted-foreground)', lineHeight: 1.5 }}>
            {t('share_document_with_projects_description')}
          </p>

          {isLoadingProjectShares ? (
            <div>{t('loading')}</div>
          ) : documentProjectShares.length === 0 ? (
            <div style={{ color: 'var(--muted-foreground)', fontSize: '0.875rem' }}>{t('no_document_project_shares')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {documentProjectShares.map((share) => (
                <div
                  key={share.id}
                  style={{
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    backgroundColor: 'var(--card)',
                    border: '1px solid var(--border)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--foreground)', marginBottom: '0.25rem', overflowWrap: 'anywhere' }}>
                      {share.projectTitle || share.projectId}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)', marginBottom: share.projectSharedWith && share.projectSharedWith.length > 0 ? '0.5rem' : 0 }}>
                      {documentPermissionLabel(share.permission)}
                    </div>
                    {share.projectSharedWith && share.projectSharedWith.length > 0 ? (
                      <div style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)', lineHeight: 1.5 }}>
                        <div style={{ marginBottom: '0.25rem' }}>{t('shared_with')}:</div>
                        <ul style={{ margin: 0, paddingLeft: '1rem', display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
                          {share.projectSharedWith.map((user) => (
                            <li key={`${share.id}-${user.userEmail}`} style={{ overflowWrap: 'anywhere' }}>
                              {user.userEmail} ({projectPermissionLabel(user.permission)})
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)' }}>
                        {t('no_project_shares')}
                      </div>
                    )}
                  </div>
                  <RotatingDeleteButton
                    onClick={() => handleDeleteProjectShare(share.id)}
                    tooltipText="Remove this share"
                    ariaLabel={t('remove') || 'Remove'}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={isShareModalOpen} onOpenChange={setIsShareModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('share_document_with_new_user')}</DialogTitle>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', paddingTop: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--foreground)' }}>
                {t('email') || 'Email'}
              </label>
              <Input
                type="email"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                placeholder={t('user_email_placeholder') || 'user@example.com'}
                autoFocus
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--foreground)' }}>
                {t('permission') || 'Permission'}
              </label>
              <Select value={sharePermission} onValueChange={(value: 'reader' | 'commenter' | 'editor') => setSharePermission(value)}>
                <SelectTrigger>
                  <span className="truncate">{documentPermissionLabel(sharePermission)}</span>
                </SelectTrigger>
                <SelectContent className={permissionSelectContentClassName}>
                  <PermissionSelectItem
                    value="reader"
                    label={t('permission_reader') || 'Reader'}
                    description={t('permission_reader_desc') || 'Can only read the document'}
                  />
                  <PermissionSelectItem
                    value="commenter"
                    label={t('permission_commenter') || 'Commenter'}
                    description={t('permission_commenter_desc') || 'Can read and comment'}
                  />
                  <PermissionSelectItem
                    value="editor"
                    label={t('permission_editor') || 'Editor'}
                    description={t('permission_editor_desc') || 'Can read, comment, and edit'}
                  />
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <CancelButton onClick={() => setIsShareModalOpen(false)}>
              {t('cancel') || 'Cancel'}
            </CancelButton>
            <Button
              onClick={handleShareWithUser}
              disabled={!shareEmail || isSharing}
              dialogPrimary
              className="inline-flex items-center gap-1.5"
            >
              {isSharing ? (
                t('sharing') || 'Sharing...'
              ) : (
                <>
                  {t('share_document')}
                  <DialogPrimaryShortcut className="ml-0.5" />
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isProjectShareModalOpen} onOpenChange={setIsProjectShareModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('share_document_with_new_project')}</DialogTitle>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', paddingTop: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--foreground)' }}>
                {t('select_project')}
              </label>
              {shareableProjects.length === 0 ? (
                <div style={{ color: 'var(--muted-foreground)', fontSize: '0.875rem' }}>{t('no_shareable_projects')}</div>
              ) : (
                <SingleSelectCombobox
                  sections={shareableProjectSections}
                  value={selectedShareProjectId}
                  onValueChange={setSelectedShareProjectId}
                  placeholder={t('select_project')}
                  searchPlaceholder={t('search_projects')}
                  emptyMessage={t('no_shareable_projects')}
                  contentClassName="z-[2010]"
                />
              )}
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--foreground)' }}>
                {t('permission') || 'Permission'}
              </label>
              <Select value={projectSharePermission} onValueChange={(value: DocumentPermission) => setProjectSharePermission(value)}>
                <SelectTrigger>
                  <span className="truncate">{documentPermissionLabel(projectSharePermission)}</span>
                </SelectTrigger>
                <SelectContent className={permissionSelectContentClassName}>
                  <PermissionSelectItem
                    value="reader"
                    label={t('permission_reader') || 'Reader'}
                    description={t('permission_reader_desc') || 'Can only read the document'}
                  />
                  <PermissionSelectItem
                    value="commenter"
                    label={t('permission_commenter') || 'Commenter'}
                    description={t('permission_commenter_desc') || 'Can read and comment'}
                  />
                  <PermissionSelectItem
                    value="editor"
                    label={t('permission_editor') || 'Editor'}
                    description={t('permission_editor_desc') || 'Can read, comment, and edit'}
                  />
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <CancelButton onClick={() => setIsProjectShareModalOpen(false)}>
              {t('cancel') || 'Cancel'}
            </CancelButton>
            <Button
              onClick={handleShareWithProject}
              disabled={!selectedShareProjectId || isSharingWithProject}
              dialogPrimary
              className="inline-flex items-center gap-1.5"
            >
              {isSharingWithProject ? (
                t('sharing') || 'Sharing...'
              ) : (
                <>
                  {t('share_document')}
                  <DialogPrimaryShortcut className="ml-0.5" />
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

